/**
 * Who may call a backend route (API_ACCESS.md §3.2, §3.3): the backend's route hook, mirrored, and
 * the rule over the subject's facts. Pure. The hook (`sales-services/node/app.js:52-64`,
 * `isContextPermitted`) reads a route's `modules` (any of them) and `roles` (the session's must be
 * one) and nothing else; a refusal is HTTP 403 `ACCESS_DENIED`. Feature checks inside handlers are
 * the policies' (§3.8), never this file's.
 *
 * Routes are declared in `@rista/core/api/routes`; this file only knows their shape (`RouteRef`),
 * so capabilities never imports the client (the dependency points one way, api → capabilities).
 */
import type { ModuleCode, ReasonCode, Role, SubjectFacts } from "./types";

/** the backend's role names: the session roles, plus `Cashier`, which one route still lists */
export type BackendRole = Role | "Cashier";

/** hosts that are not rista-be: they have no route hook, so the action's capability is their only gate */
export type ExternalHost = "recommendationEngine";

export type RouteAccess =
  /** `isPublic`: no session needed */
  | { kind: "public" }
  /** neither `modules` nor `roles` (or a misspelt `module:`): any signed-in session */
  | { kind: "session" }
  | { kind: "gated"; modules: readonly ModuleCode[]; roles: readonly BackendRole[] }
  | { kind: "external"; host: ExternalHost };

export const publicRoute = (): RouteAccess => ({ kind: "public" });
export const anySession = (): RouteAccess => ({ kind: "session" });
export const gated = ({ modules = [], roles = [] }: { modules?: readonly ModuleCode[]; roles?: readonly BackendRole[] }): RouteAccess => {
  if (!modules.length && !roles.length) throw new Error("capabilities: gated() needs modules or roles; use anySession()");
  return { kind: "gated", modules, roles };
};
export const external = (host: ExternalHost): RouteAccess => ({ kind: "external", host });

/** what the rule needs of a route: its backend key (`"POST /sale/save"`) and its access */
export interface RouteRef {
  readonly key: string;
  readonly access: RouteAccess;
}

/** why a route was refused, which decides how an action behind it is drawn (QR1, §3.5) */
export type RefusalCause = "session" | "module" | "role" | "suspended";

export type CallDecision =
  | { allowed: true }
  | {
      allowed: false;
      cause: RefusalCause;
      reason: ReasonCode;
      /** the refused route's key */
      route: string;
      /** what the route asks for, when the cause is a module or a role */
      needs?: { modules?: readonly string[]; roles?: readonly string[] };
    };

const ALLOWED: CallDecision = { allowed: true };

/**
 * The backend's hook, line for line: public and external routes pass; everything else needs a known
 * session (until the user record is in, the answer is "not yet", so an effect waits); then a listed
 * module must be held, and a listed role must be the session's.
 */
export function mayCall(route: RouteRef, subject: SubjectFacts): CallDecision {
  const { access, key } = route;
  if (access.kind === "public" || access.kind === "external") return ALLOWED;
  if (!subject.known) return { allowed: false, cause: "session", reason: "capabilities.session", route: key };
  if (access.kind === "session") return ALLOWED;
  if (access.modules.length && !access.modules.some((m) => subject.modules.has(m))) {
    return { allowed: false, cause: "module", reason: "capabilities.module", route: key, needs: { modules: access.modules } };
  }
  if (access.roles.length && !(subject.role && access.roles.includes(subject.role))) {
    return { allowed: false, cause: "role", reason: "capabilities.role", route: key, needs: { roles: access.roles } };
  }
  return ALLOWED;
}

/** each route's answer, in order, and the first refusal; `isSuspended` adds what the server refused this session */
export function checkRoutes(
  routes: readonly RouteRef[],
  subject: SubjectFacts,
  isSuspended: (key: string) => boolean = () => false,
): { decision: CallDecision; each: { key: string; decision: CallDecision }[] } {
  let decision: CallDecision = ALLOWED;
  const each = routes.map((route) => {
    let answer = mayCall(route, subject);
    if (answer.allowed && isSuspended(route.key)) {
      answer = { allowed: false, cause: "suspended", reason: "capabilities.suspended", route: route.key };
    }
    if (decision.allowed && !answer.allowed) decision = answer;
    return { key: route.key, decision: answer };
  });
  return { decision, each };
}

/** every route, in order: the first refusal, or allowed */
export function mayCallAll(
  routes: readonly RouteRef[],
  subject: SubjectFacts,
  isSuspended?: (key: string) => boolean,
): CallDecision {
  return checkRoutes(routes, subject, isSuspended).decision;
}
